import { Injectable, OnDestroy, inject } from '@angular/core';
import { StateService } from './state.service';
import { HttpClient } from '@angular/common/http';
import { NotificationStateService } from './notification.state';
import { tap, catchError } from 'rxjs/operators';
import { EMPTY } from 'rxjs';
import { EffectRef } from '@angular/core';
import { Subscription, timer, switchMap, map } from 'rxjs';
import { ApiService } from '../services/api.service';
import { ErrorHandlerService } from '../services/error-handler.service';
import { Application } from '../common.model';

export interface ApplicationState {
  applications: Application[];
  applicationsLoaded: boolean;
  selectedApplication: Application | null;
  applicationProducts: any[];
  loadingState: 'idle' | 'loading' | 'loaded';
  error: string | null;
}

export interface AddApplicationPayload {
  variant_code: string;
  server_id?: string;
  url_name?: string;
  display_name?: string;
}

const initialApplicationState: ApplicationState = {
  applications: [],
  applicationsLoaded: false,
  selectedApplication: null,
  applicationProducts: [],

  loadingState: 'idle',
  error: null,
};

@Injectable({
  providedIn: 'root',
})
export class ApplicationStateService extends StateService<ApplicationState> implements OnDestroy {
  private http = inject(HttpClient);
  private notificationService = inject(NotificationStateService);
  private apiService = inject(ApiService);
  private errorHandler = inject(ErrorHandlerService);

  readonly applications = this.selectSlice(state => state.applications);
  readonly applicationsLoaded = this.selectSlice(state => state.applicationsLoaded);
  readonly selectedApplication = this.selectSlice(state => state.selectedApplication);
  readonly applicationProducts = this.selectSlice(state => state.applicationProducts);
  readonly isLoaded = this.selectSlice(state => state.loadingState === 'loaded');
  readonly hasApplications = this.selectSlice(
    state => state.loadingState === 'loaded' && state.applications.length > 0
  );

  readonly loadingState = this.selectSlice(state => state.loadingState);
  readonly error = this.selectSlice(state => state.error);

  // Store cleanup effects
  private cleanup: EffectRef[] = [];
  private pollingSubscription?: Subscription;
  private isPolling = false;

  constructor() {
    super(initialApplicationState);
  }

  loadApplications(force = false): void {
    if (!force && this.applicationsLoaded()) {
      return;
    }
    this.setState({ loadingState: 'loading', error: null });

    this.http
      .get<any>(this.apiService.getUrl('applications'))
      .pipe(
        tap(response => {
          // Process instructions for all applications
          if (response?.applications) {
            response.applications = response.applications.map((app: Application) => {
              if (app.instructions) {
                app.instructions = this.interpolateInstructions(app);
              }
              return app;
            });
          }

          this.setState({
            applications: response?.applications || [],
            applicationsLoaded: true,
            loadingState: 'loaded',
          });

          // Start polling if any application is in installing state
          if (
            response?.applications?.some((app: Application) => this.isInstalling(app)) &&
            !this.isPolling
          ) {
            this.startPolling();
          }
        }),
        catchError(error => {
          const msg = this.errorHandler.extractErrorMessage(error);
          this.setState({ loadingState: 'loaded', error: msg });
          this.notificationService.error(msg);
          return EMPTY;
        })
      )
      .subscribe();
  }

  loadApplication(uuid: string): void {
    this.setState({ loadingState: 'loading', error: null });

    this.http
      .get<Application>(`${this.apiService.getUrl(`applications/${uuid}`)}`)
      .pipe(
        tap(application => {
          // Process instructions to interpolate variables
          if (application.instructions) {
            application.instructions = this.interpolateInstructions(application);
          }
          this.setState({ selectedApplication: application, loadingState: 'loaded' });
        }),
        catchError(error => {
          const msg = this.errorHandler.extractErrorMessage(error);
          this.setState({ loadingState: 'loaded', error: msg });
          this.notificationService.error(msg);
          return EMPTY;
        })
      )
      .subscribe();
  }

  loadApplicationProducts(): void {
    this.setState({ loadingState: 'loading', error: null });

    this.http
      .get<any[]>(this.apiService.getUrl('products/applications'))
      .pipe(
        tap(products => {
          this.setState({ applicationProducts: products, loadingState: 'loaded' });
        }),
        catchError(error => {
          const msg = this.errorHandler.extractErrorMessage(error);
          this.setState({ loadingState: 'loaded', error: msg });
          this.notificationService.error(msg);
          return EMPTY;
        })
      )
      .subscribe();
  }

  setApplicationSettings(uuid: string, data: any): void {
    this.setState({ loadingState: 'loading', error: null });

    this.http
      .put<any>(`${this.apiService.getUrl(`applications/${uuid}/settings`)}`, data)
      .pipe(
        tap(() => {
          this.setState({ loadingState: 'loaded' });
          this.notificationService.success('Settings updated successfully');
          // Reload the application to get the updated settings
          this.loadApplication(uuid);
        }),
        catchError(error => {
          const msg = this.errorHandler.extractErrorMessage(error);
          this.setState({ loadingState: 'loaded', error: msg });
          this.notificationService.error(msg);
          return EMPTY;
        })
      )
      .subscribe();
  }

  selectApplication(application: Application | null): void {
    this.setState({ selectedApplication: application });
  }

  clearSelectedApplication(): void {
    this.setState({ selectedApplication: null });
  }

  clearError(): void {
    this.setState({ error: null });
  }

  addApplication(variantCodeOrPayload: string | AddApplicationPayload, server_id?: string): void {
    this.setState({ loadingState: 'loading', error: null });
    const body: AddApplicationPayload =
      typeof variantCodeOrPayload === 'string'
        ? {
            variant_code: variantCodeOrPayload,
            ...(server_id ? { server_id } : {}),
          }
        : variantCodeOrPayload;

    this.http
      .post<any>(this.apiService.getUrl('applications'), body)
      .pipe(
        tap(() => {
          this.setState({ loadingState: 'loaded' });
          this.notificationService.success('Application added successfully');

          // Get current applications to check if we need to start polling
          this.http.get<any>(this.apiService.getUrl('applications')).subscribe(response => {
            this.setState({
              applications: response?.applications || [],
              applicationsLoaded: true,
              loadingState: 'loaded',
            });

            // Start polling only if not already polling
            if (!this.isPolling) {
              this.startPolling();
            }
          });
        }),
        catchError(error => {
          const msg = this.errorHandler.extractErrorMessage(error);
          this.setState({ loadingState: 'loaded', error: msg });
          if (!this.errorHandler.isCapacityError(error)) {
            this.notificationService.error(msg);
          }
          return EMPTY;
        })
      )
      .subscribe();
  }

  private startPolling(): void {
    if (this.pollingSubscription) {
      this.pollingSubscription.unsubscribe();
    }

    this.isPolling = true;
    let pollCount = 0;
    const MAX_POLLS = 100;

    // Store current applications to compare for status changes
    let previousApps = [...this.applications()];

    this.pollingSubscription = timer(0, 5000)
      .pipe(
        switchMap(() =>
          this.http.get<any>(this.apiService.getUrl('applications')).pipe(
            map(response => response?.applications || []),
            tap(applications => {
              pollCount++;

              // Check for applications that just completed installation
              applications.forEach((app: Application) => {
                const prevApp = previousApps.find(a => a.id === app.id);
                if (prevApp && this.isInstalling(prevApp) && !this.isInstalling(app)) {
                  // Application installation completed
                  this.notificationService.success(`${app.name_dashboard} ready`);
                }

                // Process instructions for each application
                if (app.instructions) {
                  app.instructions = this.interpolateInstructions(app);
                }
              });

              // Update previous apps for next comparison
              previousApps = [...applications];

              // Update state with new applications
              this.setState({ applications });

              // Check for completed installations
              const hasInstallingApps = applications.some((app: Application) =>
                this.isInstalling(app)
              );

              if (!hasInstallingApps || pollCount >= MAX_POLLS) {
                // Stop polling if no apps are installing or we've reached max polls
                if (this.pollingSubscription) {
                  this.pollingSubscription.unsubscribe();
                  this.isPolling = false;
                }

                if (pollCount >= MAX_POLLS && hasInstallingApps) {
                  const timeoutMessage =
                    'Application installation is taking longer than expected. Please refresh the page to check status.';
                  this.setState({
                    error: timeoutMessage,
                  });
                  this.notificationService.warning(timeoutMessage);
                }
              }
            }),
            catchError(error => {
              this.isPolling = false;
              const msg = this.errorHandler.extractErrorMessage(error);
              this.setState({ error: msg });
              this.notificationService.error(msg);
              return EMPTY;
            })
          )
        )
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    if (this.pollingSubscription) {
      this.pollingSubscription.unsubscribe();
    }
    this.cleanup.forEach(effectRef => effectRef.destroy());
  }

  // Helper method to interpolate variables in application instructions
  interpolateInstructions(app: Application): string {
    if (!app.instructions) return '';

    // Create a context object with all the app properties
    const context = {
      app: {
        id: app.id || '',
        url: app.url || '',
        name: app.name || '',
        variant_code: app.variant_code || '',
        name_dashboard: app.name_dashboard || '',
        user_url: app.user_url || '',
        status: app.status || '',
      },
    };

    // Replace ${this.app.xxx} with actual values
    return app.instructions.replace(/\${this\.app\.([\w]+)}/g, (match, prop: string) => {
      // Use type assertion with a type guard to check if the property exists
      const appContext = context.app as Record<string, string>;
      return Object.prototype.hasOwnProperty.call(appContext, prop) ? appContext[prop] : match;
    });
  }

  private isInstalling(app: Application): boolean {
    return app.status === 'installing';
  }
}
